import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const links = [
  { href: '/#start', label: 'Start' },
  { href: '/#tjanster', label: 'Tjänster' },
  { href: '/aktiviteter', label: 'Aktiviteter' },
  { href: '/#forsaljning', label: 'Försäljning' },
  { href: '/#om-oss', label: 'Om oss' },
  { href: '/#kontakt', label: 'Kontakt' },
];

export default function Navbar({ solid = false }: { solid?: boolean }) {
  const [scrolled, setScrolled] = useState(solid);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (solid) {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [solid]);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-3 sm:pt-5">
        <div
          className={`flex items-center justify-between gap-4 rounded-full border border-ink/10 px-4 sm:px-5 py-2.5 transition-all duration-300 ${
            scrolled
              ? 'bg-cream/85 backdrop-blur-xl shadow-[0_10px_40px_-20px_rgba(15,27,45,0.35)]'
              : 'bg-cream/40 backdrop-blur-md'
          }`}
        >
          <a href="/" className="flex items-center gap-2.5 group">
            <span className="relative grid place-items-center size-9 rounded-full bg-tomato text-cream shadow-pop transition-transform group-hover:-rotate-12">
              <span className="text-lg" aria-hidden>👑</span>
              <span className="absolute -right-1 -bottom-1 size-3 rounded-full bg-sun border-2 border-cream" />
            </span>
            <span className="font-display text-xl sm:text-[1.4rem] tracking-tight font-semibold">
              Borga<span className="text-tomato">kungen</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="relative px-3.5 py-2 text-sm font-medium text-ink/75 hover:text-ink transition-colors rounded-full hover:bg-ink/5"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="/#kontakt"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-ink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-grape transition-colors group"
            >
              Boka nu
              <span className="transition-transform group-hover:translate-x-0.5">→</span>
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="Öppna meny"
              aria-expanded={open}
              className="lg:hidden grid place-items-center size-10 rounded-full bg-ink text-cream"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                {open ? <><path d="M6 6l12 12" /><path d="M18 6L6 18" /></> : <><path d="M4 7h16" /><path d="M4 17h16" /></>}
              </svg>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden mt-2 rounded-3xl bg-cream border border-ink/10 shadow-pop p-3"
            >
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 rounded-2xl text-base font-medium hover:bg-sun/40"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/#kontakt"
                onClick={() => setOpen(false)}
                className="block mt-1 px-4 py-3 rounded-2xl text-base font-semibold bg-ink text-cream text-center"
              >
                Boka nu →
              </a>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
